import type {
  SkillStatus,
  SkillHistoryResponse,
  RunSkillRequest,
  ConverterRunResponse,
  ConverterHistoryResponse,
  ConverterSummaryResponse,
  ConverterMetadataResponse,
  ConverterCalendarResponse,
  FixLoopAttemptsResponse,
} from "./types";
import { API_BASE, getAuthToken, request } from "./base";

// --- Skills ---

export async function fetchSkillStatus(
  tenantId: string,
  skillName: string,
  bsda: string,
  converterType?: string
): Promise<SkillStatus> {
  const qs = new URLSearchParams({ bsda });
  if (converterType) qs.set("converterType", converterType);
  return request<SkillStatus>(
    `/api/tenants/${tenantId}/skills/${encodeURIComponent(skillName)}/status?${qs.toString()}`
  );
}

export async function fetchSkillHistory(
  tenantId: string,
  params?: { limit?: number; skill?: string }
): Promise<SkillHistoryResponse> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.skill) qs.set("skill", params.skill);
  return request<SkillHistoryResponse>(`/api/tenants/${tenantId}/skills/history?${qs.toString()}`);
}

export async function runSkill(
  tenantId: string,
  skillName: string,
  body: RunSkillRequest
): Promise<{ message: string; skill: string; status: string }> {
  return request(`/api/tenants/${tenantId}/skills/${encodeURIComponent(skillName)}/run`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function fetchSkillReport(
  tenantId: string,
  skillName: string,
  bsda: string,
  converterType?: string
): Promise<string> {
  const token = await getAuthToken();
  const qs = new URLSearchParams({ bsda });
  if (converterType) qs.set("converterType", converterType);
  const url = `${API_BASE}/tenants/${tenantId}/skills/${encodeURIComponent(skillName)}/report?${qs.toString()}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Report fetch error: ${res.status}`);
  }
  return res.text();
}

// --- Fix Loop ---

export async function fetchFixLoopAttempts(
  tenantId: string,
  bsda: string
): Promise<FixLoopAttemptsResponse> {
  return request<FixLoopAttemptsResponse>(
    `/api/tenants/${tenantId}/skills/fix_loop/attempts?bsda=${bsda}`
  );
}

// --- Converter ---

export async function runConverter(
  tenantId: string,
  body: { prompt_id?: string; file_id?: string; bsda: string }
): Promise<ConverterRunResponse> {
  return request<ConverterRunResponse>(
    `/api/tenants/${tenantId}/converter/run`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export async function fetchConverterHistory(
  tenantId: string,
  params?: { bsda?: string; limit?: number }
): Promise<ConverterHistoryResponse> {
  const qs = new URLSearchParams();
  if (params?.bsda) qs.set("bsda", params.bsda);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  return request<ConverterHistoryResponse>(
    `/api/tenants/${tenantId}/converter/history?${qs.toString()}`
  );
}

export async function fetchConverterPrompts(
  tenantId: string
): Promise<{ prompts: string[] }> {
  return request<{ prompts: string[] }>(
    `/api/tenants/${tenantId}/converter/prompts`
  );
}

export async function fetchConverterSummary(
  tenantId: string,
  days = 30
): Promise<ConverterSummaryResponse> {
  const qs = new URLSearchParams({ days: String(days) });
  return request<ConverterSummaryResponse>(
    `/api/tenants/${tenantId}/converter/summary?${qs.toString()}`
  );
}

export async function fetchConverterMetadata(
  tenantId: string,
  promptId: string,
  bsda: string
): Promise<ConverterMetadataResponse> {
  const qs = new URLSearchParams({ promptId, bsda });
  return request<ConverterMetadataResponse>(
    `/api/tenants/${tenantId}/converter/metadata?${qs.toString()}`
  );
}

export async function fetchConverterCalendar(
  tenantId: string,
  from: string,
  to: string
): Promise<ConverterCalendarResponse> {
  const qs = new URLSearchParams({ from, to });
  return request<ConverterCalendarResponse>(
    `/api/tenants/${tenantId}/converter/calendar?${qs.toString()}`
  );
}

// --- Validation Report (ralph_validate.py 결과) ---

export interface ValidationCheck {
  check_name: string;
  status: "PASS" | "FAIL" | string;
  expected?: unknown;
  actual?: unknown;
  detail?: string;
}

export interface ValidationReport {
  prompt_id: string;
  bsda: string;
  validated_at?: string;
  validation_method?: string;
  source_file?: string;
  hai_file?: string;
  checks: ValidationCheck[];
  overall_status: "PASS" | "FAIL" | string;
  overall_reason?: string;
  evidence_hash?: string;
}

export async function fetchValidationReport(
  tenantId: string,
  params: { bsda: string; prompt: string }
): Promise<ValidationReport> {
  const qs = new URLSearchParams({ bsda: params.bsda, prompt: params.prompt });
  return request<ValidationReport>(
    `/api/tenants/${tenantId}/converter/validation-report?${qs.toString()}`
  );
}

export interface ValidationSummary {
  bsda: string;
  total: number;
  pass: number;
  fail: number;
  column_mismatch: number;
  duplicate_fail: number;
  record_count_fail: number;
  prompts: Array<{
    prompt_id: string;
    overall_status: string;
    checks: Array<{ check_name: string; status: string }>;
  }>;
}

export async function fetchValidationSummary(
  tenantId: string,
  bsda: string
): Promise<ValidationSummary> {
  const qs = new URLSearchParams({ bsda });
  return request<ValidationSummary>(
    `/api/tenants/${tenantId}/converter/validation-summary?${qs.toString()}`
  );
}
